import { Fragment } from "react";
import { fmtEUR, fmtDate } from "@/lib/api";
import { shortConcept } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Ban, Tag, ChevronDown, ChevronUp, MessageSquare } from "lucide-react";
import { scoreBadge, monthLabel, SortableHead } from "./shared";

export function PendingTable({ visibleTxs, months, typeFilter, setTypeFilter, monthFilter, setMonthFilter,
  selectedTxs, canAct, selected, toggleSelect, toggleSelectAll, expanded, setExpanded, focusTx, sugMap,
  sort, clickSort, onManual, onCategorize, onNotes, onIgnore, onAccept, onBulkCategorize, onBulkClear }) {
  return (
    <>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="recon-type-filter" className="w-44 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="gastos">Solo gastos</SelectItem>
            <SelectItem value="ingresos">Solo ingresos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger data-testid="recon-month-filter" className="w-48 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los meses</SelectItem>
            {months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-400">{visibleTxs.length} movimientos</span>
        {selectedTxs.length > 0 && canAct && (
          <div className="flex items-center gap-2 ml-auto" data-testid="recon-bulk-bar">
            <Badge variant="outline" className="text-[#D9381E] border-[#D9381E]/30 bg-red-50">{selectedTxs.length} seleccionados</Badge>
            <Button size="sm" data-testid="recon-bulk-categorize" onClick={onBulkCategorize}
              className="bg-[#D9381E] hover:bg-[#B91C1C] text-white gap-1">
              <Tag className="w-3.5 h-3.5" /> Categorizar en lote
            </Button>
            <Button size="sm" variant="outline" data-testid="recon-bulk-clear" onClick={onBulkClear}>Limpiar</Button>
          </div>
        )}
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {canAct && (
                  <TableHead className="w-8">
                    <input type="checkbox" data-testid="recon-select-all" className="accent-[#D9381E]"
                      checked={visibleTxs.length > 0 && visibleTxs.every((tx) => selected[tx.id])}
                      onChange={toggleSelectAll} />
                  </TableHead>
                )}
                <SortableHead label="Fecha" sortKey="date" defDir="desc" sort={sort} clickSort={clickSort} testid="recon-sort-date" />
                <SortableHead label="Concepto" sortKey="concept" sort={sort} clickSort={clickSort} testid="recon-sort-concept" />
                <SortableHead label="Cuenta" sortKey="account" sort={sort} clickSort={clickSort} testid="recon-sort-account" />
                <SortableHead label="Importe" sortKey="amount" defDir="desc" sort={sort} clickSort={clickSort} testid="recon-sort-amount" right />
                <SortableHead label="Candidatos" sortKey="candidates" defDir="desc" sort={sort} clickSort={clickSort} testid="recon-sort-candidates" />
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleTxs.map((tx) => (
                <Fragment key={tx.id}>
                  <TableRow data-testid={`recon-row-${tx.id}`} className={`${(tx.match_candidates || []).length > 0 ? "cursor-pointer" : "cursor-default"} ${tx.id === focusTx ? "bg-amber-50 ring-1 ring-amber-300" : ""}`}
                    onClick={() => setExpanded(expanded === tx.id ? null : tx.id)}>
                    {canAct && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" data-testid={`recon-select-${tx.id}`} className="accent-[#D9381E]"
                          checked={!!selected[tx.id]} onChange={() => toggleSelect(tx.id)} />
                      </TableCell>
                    )}
                    <TableCell className="text-sm whitespace-nowrap">{fmtDate(tx.date)}</TableCell>
                    <TableCell className="text-sm max-w-96 truncate" title={tx.concept}>{shortConcept(tx.concept)}</TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap" data-testid={`recon-account-${tx.id}`}>{tx.account_name || "—"}</TableCell>
                    <TableCell className={`text-right font-num font-semibold ${tx.amount >= 0 ? "text-green-700" : "text-red-700"}`}>
                      {fmtEUR(tx.amount)}
                    </TableCell>
                    <TableCell>
                      {(tx.match_candidates || []).length > 0 ? (
                        <div className="flex gap-1.5 items-center">
                          {tx.match_candidates.slice(0, 2).map((c, i) => (
                            <Badge key={c.invoice_id ?? `${c.score}-${i}`} variant="outline" className={scoreBadge(c.score)}>{c.score}</Badge>
                          ))}
                          {expanded === tx.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        </div>
                      ) : sugMap[tx.id] ? (
                        <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50" data-testid={`recon-suggested-${tx.id}`}>
                          {sugMap[tx.id].kind === "liquidacion_tarjeta" ? `Sugerencia: TPV ${sugMap[tx.id].branch_name}` : `Sugerencia: ${sugMap[tx.id].category_name}`}
                        </Badge>
                      ) : <span className="text-xs text-slate-400">Sin match — ejecuta el matching</span>}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      {canAct && (
                        <div className="flex gap-1.5 justify-end">
                          {tx.amount < 0 && (
                            <Button data-testid={`recon-manual-${tx.id}`} size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onManual(tx); }}>Elegir facturas</Button>
                          )}
                          <Button data-testid={`recon-categorize-${tx.id}`} size="sm" variant="outline" className="gap-1" onClick={(e) => { e.stopPropagation(); onCategorize(tx); }}>
                            <Tag className="w-3.5 h-3.5" /> Categorizar
                          </Button>
                          <Button data-testid={`recon-notes-${tx.id}`} size="sm" variant="outline" className="gap-1 relative"
                            onClick={(e) => { e.stopPropagation(); onNotes(tx); }}>
                            <MessageSquare className="w-3.5 h-3.5" />
                            {(tx.notes_count || 0) > 0 && (
                              <span data-testid={`recon-notes-count-${tx.id}`}
                                className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                {tx.notes_count}
                              </span>
                            )}
                          </Button>
                          <Button data-testid={`recon-ignore-${tx.id}`} size="sm" variant="outline" className="text-slate-500" onClick={(e) => { e.stopPropagation(); onIgnore(tx.id); }}>
                            <Ban className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  {expanded === tx.id && (tx.match_candidates || []).length > 0 && (
                    <TableRow className="bg-slate-50">
                      <TableCell colSpan={canAct ? 7 : 6}>
                        <div className="space-y-2 py-1" data-testid={`recon-candidates-${tx.id}`}>
                          {tx.match_candidates.map((c, i) => (
                            <div key={c.invoice_id ?? `${c.score}-${i}`} className="flex items-center justify-between border rounded-lg px-3 py-2 bg-white">
                              <div className="text-sm">
                                <Badge variant="outline" className={`${scoreBadge(c.score)} mr-2`}>{c.score}</Badge>
                                <span className="font-medium">{c.supplier_name}</span>
                                <span className="text-slate-400 ml-2 text-xs font-num">{c.invoice_number}</span>
                                {c.invoice_date && (
                                  <span className="text-slate-400 ml-2 text-xs font-num" data-testid={`recon-candidate-date-${tx.id}-${i}`}>{c.invoice_date}</span>
                                )}
                                {c.type === "grouped" && <Badge variant="outline" className="ml-2 text-blue-700 border-blue-200 bg-blue-50">Pago agrupado</Badge>}
                                {c.type === "amount_only" && <Badge variant="outline" className="ml-2 text-amber-700 border-amber-200 bg-amber-50">Solo importe — verifica</Badge>}
                                {c.type === "date_match" && <Badge variant="outline" className="ml-2 text-teal-700 border-teal-200 bg-teal-50" data-testid={`recon-date-match-${tx.id}-${i}`}>Por fecha — verifica</Badge>}
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="font-num text-sm">{fmtEUR(c.pending)}</span>
                                {canAct && (
                                  <Button data-testid={`recon-accept-${tx.id}-${i}`} size="sm"
                                    className="bg-green-700 hover:bg-green-800 text-white"
                                    onClick={() => onAccept(tx, c)}>
                                    Conciliar
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
              {visibleTxs.length === 0 && (
                <TableRow><TableCell colSpan={canAct ? 7 : 6} className="text-center py-10 text-slate-400 text-sm">
                  No hay movimientos pendientes de conciliar.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
